import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, CheckCircle2, ThumbsUp, ThumbsDown } from "lucide-react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import { tellMeMore, flagSimplification } from "@/lib/api";
import { useRole } from "@/contexts/RoleContext";
import TM from "@/components/TM";

/**
 * "Tell me more" button + response card used on Explanation, Guided-Review Step 4,
 * and ReviseRecap Summary screens. Never affects scoring/routing/Engine.
 *
 * Props
 *   - scope: "explanation" | "guided_review_step4" | "recap_summary"
 *   - recordId: string
 *   - subject: "Mathematics" | "English" | "Hindi" | "EVS"  → controls card tint
 */

// Subject → soft warm tint (background, border, accent dot).
const TINT = {
  Mathematics: { bg: "bg-sky-50",     border: "border-sky-200",    accent: "text-sky-700",    dot: "bg-sky-500"    },
  English:     { bg: "bg-emerald-50", border: "border-emerald-200",accent: "text-emerald-700",dot: "bg-emerald-500"},
  Hindi:       { bg: "bg-orange-50",  border: "border-orange-200", accent: "text-orange-700", dot: "bg-orange-500" },
  EVS:         { bg: "bg-teal-50",    border: "border-teal-200",   accent: "text-teal-700",   dot: "bg-teal-500"   },
};
const DEFAULT_TINT = { bg: "bg-sky-50", border: "border-sky-200", accent: "text-sky-700", dot: "bg-sky-500" };

// Configure marked once: GFM, line-breaks, no headings/blockquotes/list nesting beyond inline.
marked.setOptions({ gfm: true, breaks: true });

// Sanitize then return HTML for inline rendering. DOMPurify allows only safe inline tags.
function renderInlineHTML(mdText) {
  if (!mdText) return "";
  const html = marked.parseInline(mdText, { async: false });
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["strong", "b", "em", "i", "code", "br", "u"],
    ALLOWED_ATTR: [],
  });
}

// Split the (raw, pre-markdown) text into sentence-level lines.
// Numbered steps like "1." / "2." stay attached to their sentence.
function splitIntoLines(text) {
  if (!text) return [];
  // Honour any newlines the model already produced first.
  const byLines = text.replace(/\r\n/g, "\n").split(/\n+/).map((s) => s.trim()).filter(Boolean);
  const out = [];
  for (const line of byLines) {
    // Split on sentence boundaries (.!?) followed by whitespace + opener (capital / digit / quote).
    // The lookahead protects decimals like "3.14" and numbered lists like "1. step" (digit comes BEFORE the period).
    const parts = line.split(/(?<=[.!?])\s+(?=[A-Z0-9"'(\u201C])/);
    for (const p of parts) {
      const t = p.trim();
      if (t) out.push(t);
    }
  }
  return out;
}

export default function TellMeMoreButton({ scope, recordId, subject, wrongOptionChosen, label }) {
  const { learner } = useRole();
  const [text, setText] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qcDismissed, setQcDismissed] = useState(false);

  const tint = TINT[subject] || DEFAULT_TINT;

  const onClick = async () => {
    if (text) { setText(null); return; }
    setLoading(true); setError(null);
    try {
      const res = await tellMeMore({ scope, recordId, learnerId: learner?.learner_id, wrongOptionChosen });
      setText(res?.text || "");
    } catch (err) {
      setError(err?.response?.data?.detail || "Mitraa™ is thinking… try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  // Only show the trigger button when no response is open. Once a response is open,
  // the "Got it" pill lives inside the card (bottom-right) per spec.
  if (!text) {
    return (
      <div className="mt-4" data-testid={`tell-me-more-${scope}`}>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onClick}
          disabled={loading}
          className={`rounded-full border-2 font-semibold text-sm tap-44 px-4 py-2 ${tint.border} ${tint.accent} hover:opacity-90`}
          data-testid={`tell-me-more-btn-${scope}`}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded-full border-2 border-current border-t-transparent animate-spin" />
              Mitraa<TM /> is thinking…
            </span>
          ) : (
            <span className="flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> {label || "Tell me more"}</span>
          )}
        </Button>
        {error && (
          <p
            className="mt-3 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 max-w-[480px]"
            data-testid={`tell-me-more-error-${scope}`}
          >
            {error}
          </p>
        )}
      </div>
    );
  }

  const lines = splitIntoLines(text);
  return (
    <div className="mt-4 flex justify-center" data-testid={`tell-me-more-${scope}`}>
      <div
        className={`relative w-full max-w-[480px] rounded-2xl shadow-md ${tint.bg} ${tint.border} border fade-up`}
        style={{ padding: "16px" }}
        data-testid={`tell-me-more-card-${scope}`}
      >
        <div className="flex items-center gap-2 mb-3">
          <div className={`w-7 h-7 rounded-full bg-white flex items-center justify-center shadow-sm`}>
            <Sparkles className={`w-4 h-4 ${tint.accent}`} />
          </div>
          <span className={`text-xs font-bold uppercase tracking-wider ${tint.accent}`} data-testid={`tell-me-more-label-${scope}`}>
            Mitraa<TM /> says
          </span>
        </div>

        <div className="text-slate-800" style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {lines.map((line, idx) => (
            <p
              key={idx}
              className="m-0"
              style={{ fontSize: "15px", lineHeight: 1.6 }}
              data-testid={`tell-me-more-line-${scope}-${idx}`}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: renderInlineHTML(line) }}
            />
          ))}
        </div>

        {/* QC flag — only on guided_review_step4 */}
        {scope === "guided_review_step4" && !qcDismissed && (
          <div className="mt-4 flex items-center gap-3" data-testid="simplify-qc-row">
            <span className="text-xs text-slate-500 font-medium">Did this help?</span>
            <button
              type="button"
              onClick={() => setQcDismissed(true)}
              className={`flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full border ${tint.border} ${tint.accent} hover:opacity-80 transition-opacity`}
              data-testid="simplify-qc-yes"
            >
              <ThumbsUp className="w-3 h-3" /> Yes
            </button>
            <button
              type="button"
              onClick={() => {
                flagSimplification(recordId, wrongOptionChosen, text).catch(() => {});
                setQcDismissed(true);
              }}
              className="flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full border border-slate-300 text-slate-500 hover:opacity-80 transition-opacity"
              data-testid="simplify-qc-not-really"
            >
              <ThumbsDown className="w-3 h-3" /> Not really
            </button>
          </div>
        )}

        <div className="flex justify-end mt-4">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setText(null)}
            className={`rounded-full border-2 font-semibold text-sm tap-44 px-4 py-2 ${tint.border} ${tint.accent} hover:opacity-90`}
            data-testid={`tell-me-more-btn-${scope}`}
          >
            <span className="flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5" /> Got it</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
